import React from 'react';
import { ArrowRight } from 'lucide-react';
import { trackCTAClick } from '../utils/analytics';

const stages = [
  {
    step: '01',
    label: 'Engagement',
    image: '/images/sbdc-seminar.jpg',
    caption: 'Executive Coaching',
    metric: 'Day 1: Building trust and setting the foundation',
    description: 'We meet your team where they are, creating a safe space for growth.',
  },
  {
    step: '02',
    label: 'Discovery',
    image: '/images/disc-assessment.jpg',
    caption: 'DISC Assessment',
    metric: '87% of teams discover blind spots in communication style',
    description: 'Through behavioral assessments, we uncover the dynamics that drive or hinder performance.',
  },
  {
    step: '03',
    label: 'Application',
    image: '/images/group-exercise.jpg',
    caption: 'Team Exercise',
    metric: 'Real-time practice in collaborative environments',
    description: 'Interactive sessions turn knowledge into skills through hands-on practice.',
  },
  {
    step: '04',
    label: 'Alignment',
    image: '/images/alignment-workshop.jpg',
    caption: 'Team Alignment',
    metric: '92% of participants report improved collaboration',
    description: 'Teams develop shared language, shared goals, and shared accountability.',
  },
  {
    step: '05',
    label: 'Results',
    image: '/images/tapestry-group.jpg',
    caption: 'Leadership Training',
    metric: 'Measurable improvement within 30 days',
    description: 'Organizations see lasting change in communication, retention, and performance.',
  },
];

const Transformation = () => {
  const scrollToConsultation = () => {
    trackCTAClick('Start your transformation', 'transformation');
    document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="transformation" className="py-16 sm:py-24 bg-slate-950" data-testid="transformation-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            The Journey
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            This Is What Transformation
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-emerald-400 to-cyan-400">
              Looks Like
            </span>
          </h2>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
            From first conversation to lasting impact — see how organizations move from challenge to alignment.
          </p>
        </div>

        {/* Timeline */}
        <div className="space-y-6">
          {stages.map((s, i) => (
            <div
              key={i}
              className={`group grid md:grid-cols-2 gap-5 md:gap-8 items-center ${i % 2 === 1 ? 'md:direction-rtl' : ''}`}
              data-testid={`transformation-stage-${i}`}
            >
              {/* Image */}
              <div className={`${i % 2 === 1 ? 'md:order-2' : ''} relative overflow-hidden rounded-xl`}>
                <div className="aspect-[16/10]">
                  <img
                    src={s.image}
                    alt={s.caption}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                {/* Caption Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="px-2.5 py-1 rounded-md bg-white/10 backdrop-blur-sm text-white text-xs font-semibold border border-white/20">
                    {s.caption}
                  </span>
                  <p className="text-emerald-400 text-xs font-medium mt-2">{s.metric}</p>
                </div>
              </div>

              {/* Content */}
              <div className={`${i % 2 === 1 ? 'md:order-1' : ''}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-bold text-slate-500 tracking-wider">{s.step}</span>
                  <div className="h-px flex-1 bg-gradient-to-r from-slate-700 to-transparent" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{s.label}</h3>
                <p className="text-slate-400 text-base leading-relaxed">{s.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Connecting Line (visual) */}
        <div className="hidden md:flex justify-center mt-8">
          <div className="flex items-center gap-2 text-slate-500">
            <div className="h-px w-8 bg-gradient-to-r from-transparent to-emerald-500" />
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Transformation Complete</span>
            <div className="h-px w-8 bg-gradient-to-l from-transparent to-emerald-500" />
          </div>
        </div>

        {/* Micro CTA */}
        <div className="text-center mt-10">
          <button
            onClick={scrollToConsultation}
            data-testid="transformation-cta"
            className="inline-flex items-center gap-2 text-white font-semibold hover:text-cyan-400 transition-colors text-sm group/cta"
          >
            Let's build this for your organization
            <ArrowRight className="h-4 w-4 group-hover/cta:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Transformation;
